'use client';

import React, { useState, useRef } from 'react';
import { UploadCloud, Image as ImageIcon, X, Loader2, Link as LinkIcon, Check } from 'lucide-react';

interface ImageUploadDropzoneProps {
  value?: string | null;
  onChange: (url: string) => void;
  label?: string;
  helperText?: string;
  aspect?: 'square' | 'cover' | 'video' | 'compact';
  isRoundAvatar?: boolean;
  className?: string;
}

export function ImageUploadDropzone({
  value,
  onChange,
  label,
  helperText,
  aspect = 'cover',
  isRoundAvatar = false,
  className = ''
}: ImageUploadDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadFile = async (file: File) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setUploadError('Por favor seleccioná un archivo de imagen válido (JPG, PNG, WEBP).');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setUploadError('La imagen no debe superar los 10MB.');
      return;
    }

    setUploadError(null);
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch('/api/upload', {
        method: 'POST',
        body: formData
      });

      const data = await res.json();
      if (data.success && data.url) {
        onChange(data.url);
      } else {
        setUploadError(data.error || 'Error al subir la imagen.');
      }
    } catch (err: any) {
      console.error('Error subiendo imagen:', err);
      setUploadError('No se pudo subir la imagen. Verificá tu conexión.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      handleUploadFile(file);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      handleUploadFile(file);
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleApplyUrl = (e?: React.FormEvent | React.MouseEvent | React.KeyboardEvent) => {
    if (e) e.preventDefault();
    if (urlDraft.trim()) {
      onChange(urlDraft.trim());
      setShowUrlInput(false);
      setUrlDraft('');
    }
  };

  // Dimensiones según relación de aspecto
  const getContainerClasses = () => {
    if (aspect === 'square') {
      return isRoundAvatar ? 'w-28 h-28 rounded-full' : 'w-28 h-28 sm:w-32 sm:h-32 rounded-2xl';
    }
    if (aspect === 'compact') {
      return 'w-full h-32 rounded-2xl';
    }
    // cover / video
    return 'w-full h-44 sm:h-52 rounded-2xl';
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <div className="flex items-center justify-between">
          <label className="block text-xs font-bold text-slate-700">
            {label}
          </label>
          <button
            type="button"
            onClick={() => setShowUrlInput(!showUrlInput)}
            className="text-[11px] text-slate-400 hover:text-slate-600 font-medium inline-flex items-center gap-1 cursor-pointer transition-colors"
          >
            <LinkIcon className="w-3 h-3" />
            <span>{showUrlInput ? 'Cerrar enlace' : 'O usar URL'}</span>
          </button>
        </div>
      )}

      {/* Input de archivo oculto */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Input alternativo por URL directa (sin usar <form> para evitar anidamiento ilegal en HTML) */}
      {showUrlInput && (
        <div className="flex items-center gap-2 mb-2 animate-in fade-in">
          <input
            type="url"
            placeholder="Pegar https://... de una imagen"
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleApplyUrl(e);
              }
            }}
            className="flex-1 px-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 font-medium"
          />
          <button
            type="button"
            onClick={handleApplyUrl}
            className="px-3 py-1.5 bg-slate-800 hover:bg-black text-white text-xs font-semibold rounded-xl transition cursor-pointer"
          >
            Aplicar
          </button>
        </div>
      )}

      {/* Zona con vista previa O dropzone vacío */}
      {value ? (
        <div className={`relative group overflow-hidden border border-slate-200 bg-slate-50 shadow-xs ${getContainerClasses()}`}>
          <img
            src={value}
            alt={label || 'Imagen cargada'}
            className="w-full h-full object-cover object-center"
          />

          {/* Overlay de acciones al hacer hover */}
          <div className="absolute inset-0 bg-slate-950/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 p-2 backdrop-blur-2xs">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-3 py-1.5 bg-white text-slate-800 hover:bg-slate-100 text-[11px] font-bold rounded-lg shadow-sm transition cursor-pointer active:scale-95"
            >
              Cambiar
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="p-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg shadow-sm transition cursor-pointer active:scale-95"
              title="Eliminar imagen"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !isUploading && fileInputRef.current?.click()}
          className={`relative border-2 border-dashed transition-all cursor-pointer flex flex-col items-center justify-center p-4 text-center ${getContainerClasses()} ${
            isDragging
              ? 'border-emerald-600 bg-emerald-50/70 scale-[1.01]'
              : 'border-slate-200 hover:border-slate-300 bg-slate-50/60 hover:bg-slate-50'
          }`}
        >
          {isUploading ? (
            <div className="flex flex-col items-center gap-2 text-slate-600">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
              <span className="text-xs font-bold">Subiendo imagen...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1.5">
              <div className="p-2.5 rounded-full bg-white shadow-2xs text-slate-500 border border-slate-100">
                <UploadCloud className="w-5 h-5 text-slate-600" />
              </div>
              <div className="space-y-0.5">
                <p className="text-xs font-bold text-slate-800">
                  {aspect === 'square' ? 'Subir foto' : 'Hacé click o arrastrá una imagen'}
                </p>
                <p className="text-[10px] text-slate-400">
                  JPG, PNG o WEBP (hasta 10MB)
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mensaje de error si falla la subida */}
      {uploadError && (
        <p className="text-xs text-rose-600 font-medium animate-in fade-in">
          ⚠️ {uploadError}
        </p>
      )}

      {/* Texto de ayuda secundario */}
      {helperText && !uploadError && (
        <p className="text-[11px] text-slate-400">
          {helperText}
        </p>
      )}
    </div>
  );
}
